import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import CommonHeader from '../../util_components/common_header';
import * as actions from '../../../redux/action_creators';
import { api_error_reason, fetch_roster } from './api';
import type { TeamMember, TeamRoster } from './api';
import {
  format_next_leave,
  format_shift,
  format_weekly_off,
  sort_roster,
  status_badges
} from './roster';
import type { BadgeTone } from './roster';
import EditRepModal from './EditRepModal';
import type { Notify } from './EditRepModal';

const PAGE_STYLE: React.CSSProperties = {
  background: 'var(--surface-page)',
  color: 'var(--text-primary)',
  minHeight: '100%'
};
const CARD_STYLE: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderColor: 'var(--border-default)'
};
const MUTED_STYLE: React.CSSProperties = { color: 'var(--text-muted)' };

const TONE_CLASS: Record<BadgeTone, string> = {
  success: 'bg-state-success-bg text-state-success-text',
  warning: 'bg-state-warning-bg text-state-warning-text',
  muted: 'bg-surface-sunken text-content-muted'
};

const COLUMNS = ['Rep', 'Level', 'Shift', 'Weekly off', 'Next leave', 'Status', 'On shift now'];

interface Props {
  notify: Notify;
}

export function SalesTeamPage({ notify }: Props) {
  const [roster, set_roster] = useState<TeamRoster | null>(null);
  const [loading, set_loading] = useState(true);
  const [editing, set_editing] = useState<TeamMember | null>(null);
  const [load_error, set_load_error] = useState<string | null>(null);

  const load = useCallback(() => {
    set_loading(true);
    fetch_roster()
      .then((r) => {
        set_roster(r);
        set_load_error(null);
      })
      .catch((e: unknown) => {
        const reason = api_error_reason(e, 'Failed to load sales team');
        set_load_error(reason);
        notify('error', reason);
      })
      .finally(() => set_loading(false));
  }, [notify]);

  useEffect(load, [load]);

  const on_close = (changed: boolean) => {
    set_editing(null);
    if (changed) load();
  };

  const can_edit = !!roster?.can_edit;
  const members = sort_roster(roster?.members);
  const col_count = COLUMNS.length + (can_edit ? 1 : 0);

  const render_row = (m: TeamMember) => (
    <tr
      key={m.ops_uuid}
      className="border-t border-line"
      style={m.status === 'LEFT' ? { opacity: 0.6 } : undefined}
    >
      <td className="px-4 py-3 text-left">
        <div className="font-medium">{m.name || m.email || m.ops_uuid}</div>
        {m.email ? (
          <div className="text-xs" style={MUTED_STYLE}>
            {m.email}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3 text-left">{m.level || <span style={MUTED_STYLE}>Not set</span>}</td>
      <td className="whitespace-nowrap px-4 py-3 text-left">
        {format_shift(m.shift_start, m.shift_end)}
      </td>
      <td className="px-4 py-3 text-left">{format_weekly_off(m.weekly_off)}</td>
      <td className="px-4 py-3 text-left">{format_next_leave(m.next_leave)}</td>
      <td className="px-4 py-3 text-left">
        <div className="flex flex-wrap gap-1">
          {status_badges(m).map((b) => (
            <span
              key={b.key}
              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[b.tone]}`}
            >
              {b.label}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3 text-left">
        {m.on_shift_now ? (
          <span className="inline-block rounded-full bg-state-success-bg px-2 py-0.5 text-xs font-medium text-state-success-text">
            On shift
          </span>
        ) : (
          <span style={MUTED_STYLE}>—</span>
        )}
      </td>
      {can_edit ? (
        <td className="px-4 py-3 text-left">
          <button
            type="button"
            onClick={() => set_editing(m)}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={{
              background: 'var(--brand-primary)',
              color: 'var(--text-on-brand)',
              border: 'none',
              margin: 0
            }}
          >
            Edit
          </button>
        </td>
      ) : null}
    </tr>
  );

  const render_body = () => {
    if (load_error && !roster) {
      return (
        <tr>
          <td colSpan={col_count} className="px-4 py-8 text-center">
            <div role="alert" style={{ color: 'var(--state-error)' }}>
              Could not load the sales team: {load_error}
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="mt-3 rounded-md px-3 py-1.5 text-xs font-medium"
              style={{
                background: 'var(--brand-primary)',
                color: 'var(--text-on-brand)',
                border: 'none',
                margin: 0
              }}
            >
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          </td>
        </tr>
      );
    }
    if (loading && !roster) {
      return (
        <tr>
          <td colSpan={col_count} className="px-4 py-8 text-center" style={MUTED_STYLE}>
            Loading sales team…
          </td>
        </tr>
      );
    }
    if (members.length === 0) {
      return (
        <tr>
          <td colSpan={col_count} className="px-4 py-8 text-center" style={MUTED_STYLE}>
            No sales reps found. Reps appear here once they have the SALES role.
          </td>
        </tr>
      );
    }
    return members.map(render_row);
  };

  return (
    <div style={PAGE_STYLE}>
      <CommonHeader title="Sales Team" />
      <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6">
        <section className="rounded-lg border" style={CARD_STYLE}>
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="font-semibold">Reps</h2>
            <span className="text-xs" style={MUTED_STYLE}>
              Shifts in IST · {can_edit ? 'you can edit' : 'read only'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-sunken text-content-secondary">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c} className="px-4 py-2 text-left font-medium">
                      {c}
                    </th>
                  ))}
                  {can_edit ? <th className="px-4 py-2 text-left font-medium">Action</th> : null}
                </tr>
              </thead>
              <tbody>{render_body()}</tbody>
            </table>
          </div>
        </section>
      </div>
      {editing ? <EditRepModal member={editing} notify={notify} onClose={on_close} /> : null}
    </div>
  );
}

type Dispatch = (action: unknown) => void;

const mapDispatchToProps = (dispatch: Dispatch) => ({
  notify: (type: 'success' | 'error', message: string) =>
    dispatch(actions.set_notification_variable(true, type, message))
});

const ConnectedSalesTeamPage = connect(null, mapDispatchToProps)(SalesTeamPage);

export default ConnectedSalesTeamPage;
